import React, { useState, useEffect } from 'react';
import { Percent, Plus, Trash2, Save, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDiscounts = () => {
  const [config, setConfig] = useState({
    isEnabled: true,
    tiers: [
      { minServices: 2, discountPercent: 5.0 },
      { minServices: 3, discountPercent: 10.0 },
      { minServices: 4, discountPercent: 15.0 },
    ]
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/discount-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching discount config:', error);
      showMessage('error', 'Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleToggleEnabled = () => {
    setConfig(prev => ({ ...prev, isEnabled: !prev.isEnabled }));
  };

  const handleTierChange = (index, field, value) => {
    const newTiers = [...config.tiers];
    newTiers[index] = {
      ...newTiers[index],
      [field]: field === 'minServices' ? parseInt(value) || 0 : parseFloat(value) || 0
    };
    setConfig(prev => ({ ...prev, tiers: newTiers }));
  };

  const handleAddTier = () => {
    const maxMinServices = Math.max(...config.tiers.map(t => t.minServices), 0);
    setConfig(prev => ({
      ...prev,
      tiers: [...prev.tiers, { minServices: maxMinServices + 1, discountPercent: 0 }]
    }));
  };

  const handleRemoveTier = (index) => {
    if (config.tiers.length <= 1) {
      showMessage('error', 'Vous devez garder au moins un palier de réduction');
      return;
    }
    setConfig(prev => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    // Validation
    if (config.tiers.some(t => t.minServices < 1)) {
      showMessage('error', 'Le nombre minimum de services doit être au moins 1');
      return;
    }

    if (config.tiers.some(t => t.discountPercent < 0 || t.discountPercent > 100)) {
      showMessage('error', 'Le pourcentage de réduction doit être entre 0 et 100');
      return;
    }

    // Trier les paliers par minServices
    const sortedTiers = [...config.tiers].sort((a, b) => a.minServices - b.minServices);

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/discount-config`,
        { isEnabled: config.isEnabled, tiers: sortedTiers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage('success', 'Configuration enregistrée avec succès !');
      setConfig(prev => ({ ...prev, tiers: sortedTiers }));
    } catch (error) {
      console.error('Error saving config:', error);
      showMessage('error', 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A96E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-serif font-bold text-[#2C1810] mb-2">
            Configuration des Réductions
          </h1>
          <p className="text-[#5D4037]">
            Gérez les réductions pour les réservations multi-services
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-200'
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Enable/Disable Toggle */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-[#2C1810] mb-1">
                Système de réductions
              </h2>
              <p className="text-sm text-[#5D4037]">
                Activez ou désactivez les réductions automatiques pour les réservations multiples
              </p>
            </div>
            <button
              onClick={handleToggleEnabled}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                config.isEnabled ? 'bg-[#C9A96E]' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  config.isEnabled ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Tiers Configuration */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-[#2C1810] flex items-center gap-2">
              <Percent className="text-[#C9A96E]" size={24} />
              Paliers de réduction
            </h2>
            <button
              onClick={handleAddTier}
              className="flex items-center gap-2 bg-[#C9A96E] text-white px-4 py-2 rounded-lg hover:bg-[#A67B5B] transition-colors"
            >
              <Plus size={18} />
              Ajouter un palier
            </button>
          </div>

          <div className="space-y-4">
            {config.tiers.map((tier, index) => (
              <div
                key={index}
                className="bg-[#FDF8F3] rounded-xl p-4 flex items-center gap-4"
              >
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2C1810] mb-2">
                      Nombre minimum de services
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={tier.minServices}
                      onChange={(e) => handleTierChange(index, 'minServices', e.target.value)}
                      className="w-full px-4 py-2 border border-[#D4C4B5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A96E]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C1810] mb-2">
                      Réduction (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={tier.discountPercent}
                      onChange={(e) => handleTierChange(index, 'discountPercent', e.target.value)}
                      className="w-full px-4 py-2 border border-[#D4C4B5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A96E]"
                    />
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveTier(index)}
                  className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                  title="Supprimer ce palier"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ))}
          </div>

          {config.tiers.length === 0 && (
            <div className="text-center py-8 text-[#5D4037]">
              Aucun palier configuré. Ajoutez-en un pour commencer.
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-[#2C1810] mb-4">
            Aperçu des réductions
          </h2>
          <div className="space-y-2">
            {config.isEnabled ? (
              config.tiers
                .sort((a, b) => a.minServices - b.minServices)
                .map((tier, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-3 px-4 bg-[#FDF8F3] rounded-lg"
                  >
                    <span className="text-[#2C1810]">
                      {tier.minServices === 1
                        ? '1 service'
                        : `${tier.minServices} services ou plus`}
                    </span>
                    <span className="font-bold text-[#C9A96E]">
                      {tier.discountPercent}% de réduction
                    </span>
                  </div>
                ))
            ) : (
              <p className="text-center text-[#5D4037] py-4">
                Les réductions sont désactivées
              </p>
            )}
          </div>
        </div>

        {/* Example */}
        {config.isEnabled && config.tiers.length > 0 && (
          <div className="bg-gradient-to-r from-[#C9A96E]/10 to-[#A67B5B]/10 rounded-2xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-[#2C1810] mb-3">
              💡 Exemple
            </h3>
            <p className="text-[#5D4037] mb-2">
              Si un client réserve 3 services pour un total de 160€ :
            </p>
            <div className="bg-white/70 rounded-lg p-4 space-y-1">
              {(() => {
                const sortedTiers = [...config.tiers].sort((a, b) => b.minServices - a.minServices);
                const applicableTier = sortedTiers.find(t => 3 >= t.minServices);
                const discount = applicableTier ? (160 * applicableTier.discountPercent / 100) : 0;
                const total = 160 - discount;

                return (
                  <>
                    <div className="flex justify-between text-[#2C1810]">
                      <span>Sous-total :</span>
                      <span>160€</span>
                    </div>
                    {applicableTier && (
                      <div className="flex justify-between text-[#C9A96E] font-semibold">
                        <span>Réduction ({applicableTier.discountPercent}%) :</span>
                        <span>-{discount.toFixed(2)}€</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold text-[#2C1810] pt-2 border-t border-[#D4C4B5]">
                      <span>Total :</span>
                      <span>{total.toFixed(2)}€</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#2C1810] text-white px-8 py-3 rounded-lg hover:bg-[#1a0f08] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={20} />
                Enregistrer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
